import { axiosInstance } from "./api";

export type RenderQuality = "LQ" | "MQ" | "HQ";

export type RenderQualityCaps = {
  pixelRatio: number;
  postProcessingScale: number;
  shadowMapSize: number;
};

export const RUNTIME_MEDIA_FAMILIES = ["image", "audio", "video", "model3d"] as const;
export type RuntimeMediaFamily = typeof RUNTIME_MEDIA_FAMILIES[number];
export type RuntimeMediaDelivery = {
  canonicalDomain: string;
  cdnDomain: string;
  families: readonly RuntimeMediaFamily[];
};
export type RuntimeMediaManifest = {
  schema: "artetra.runtimeMedia.v1";
  family: RuntimeMediaFamily;
  source: {
    url: string;
    sha256: string;
  };
  asset: {
    url: string;
    sha256: string;
    byteLength: number;
    contentType: string;
  };
};

export type RuntimeConfig = {
  version: string;
  source: "code-defaults" | "api" | "fallback";
  renderer: {
    defaultQualityProfile: RenderQuality;
    maxPixelRatio: number;
    allowHighQuality: boolean;
    qualityCaps: Record<RenderQuality, RenderQualityCaps>;
  };
  telemetry: {
    galleryPerformanceEnabled: boolean;
    galleryPerformanceSampleRate: number;
  };
  optimizedAssets: { enabled: boolean };
  mediaDelivery: RuntimeMediaDelivery | null;
  progressiveLoading: { enabled: boolean };
  killSwitches: {
    disable3DGallery: boolean;
    disableGalleryTelemetry: boolean;
  };
};

export const DEFAULT_RUNTIME_CONFIG: RuntimeConfig = {
  version: "ART-556-runtime-config-v1",
  source: "fallback",
  renderer: {
    defaultQualityProfile: "MQ",
    maxPixelRatio: 1.5,
    allowHighQuality: true,
    qualityCaps: {
      LQ: { pixelRatio: 1, postProcessingScale: 0.6, shadowMapSize: 0 },
      MQ: { pixelRatio: 1.25, postProcessingScale: 1, shadowMapSize: 1024 },
      HQ: { pixelRatio: 1.5, postProcessingScale: 1.25, shadowMapSize: 2048 },
    },
  },
  telemetry: {
    galleryPerformanceEnabled: true,
    galleryPerformanceSampleRate: 1,
  },
  optimizedAssets: { enabled: false },
  mediaDelivery: null,
  progressiveLoading: { enabled: false },
  killSwitches: {
    disable3DGallery: false,
    disableGalleryTelemetry: false,
  },
};

const QUALITY_PROFILES: RenderQuality[] = ["LQ", "MQ", "HQ"];

const isFiniteRange = (value: unknown, min: number, max: number) =>
  typeof value === "number" && Number.isFinite(value) && value >= min && value <= max;

const isQualityCaps = (value: unknown): value is RenderQualityCaps => {
  if (!value || typeof value !== "object") return false;
  const caps = value as Partial<RenderQualityCaps>;
  return isFiniteRange(caps.pixelRatio, 0, 4)
    && isFiniteRange(caps.postProcessingScale, 0, 2)
    && isFiniteRange(caps.shadowMapSize, 0, 8192)
    && Number.isInteger(caps.shadowMapSize);
};

const isPrivateIpv4 = (hostname: string): boolean => {
  const parts = hostname.split(".");
  if (parts.length !== 4 || parts.some((part) => !/^\d{1,3}$/.test(part))) return false;
  const octets = parts.map(Number);
  if (octets.some((octet) => octet > 255)) return true;
  return octets[0] === 0
    || octets[0] === 10
    || octets[0] === 127
    || (octets[0] === 100 && octets[1] >= 64 && octets[1] <= 127)
    || (octets[0] === 169 && octets[1] === 254)
    || (octets[0] === 172 && octets[1] >= 16 && octets[1] <= 31)
    || (octets[0] === 192 && octets[1] === 168)
    || (octets[0] === 198 && (octets[1] === 18 || octets[1] === 19))
    || octets[0] >= 224;
};

const isPrivateHostname = (hostname: string): boolean => {
  const normalized = hostname.toLowerCase().replace(/\.$/, "").replace(/^\[|\]$/g, "");
  if (!normalized || normalized === "localhost" || normalized.endsWith(".localhost")) return true;
  // Runtime media origins are DNS names. Reject every IP-literal IPv6 form,
  // including IPv4-mapped loopback/private addresses.
  if (normalized.includes(":")) return true;
  return isPrivateIpv4(normalized);
};

const isPublicHttpsBaseUrl = (value: unknown): value is string => {
  if (typeof value !== "string" || value !== value.trim()) return false;
  try {
    const url = new URL(value);
    const hostname = url.hostname.toLowerCase();
    return url.protocol === "https:"
      && !url.username
      && !url.password
      && !url.search
      && !url.hash
      && url.pathname === "/"
      && !isPrivateHostname(hostname);
  } catch (_error) {
    return false;
  }
};

const isRuntimeMediaDelivery = (value: unknown): value is RuntimeMediaDelivery => {
  if (value === null) return true;
  if (!value || typeof value !== "object") return false;
  const delivery = value as Partial<RuntimeMediaDelivery>;
  if (
    !isPublicHttpsBaseUrl(delivery.canonicalDomain)
    || !isPublicHttpsBaseUrl(delivery.cdnDomain)
    || !Array.isArray(delivery.families)
  ) return false;
  if (new URL(delivery.canonicalDomain).origin === new URL(delivery.cdnDomain).origin) {
    return false;
  }
  const uniqueFamilies = new Set(delivery.families);
  return uniqueFamilies.size === delivery.families.length
    && delivery.families.every((family) => RUNTIME_MEDIA_FAMILIES.includes(family));
};

const isRuntimeConfig = (value: unknown): value is RuntimeConfig => {
  if (!value || typeof value !== "object") return false;
  const config = value as RuntimeConfig;
  return typeof config.version === "string"
    && config.version.length > 0
    && Boolean(config.renderer)
    && QUALITY_PROFILES.includes(config.renderer.defaultQualityProfile)
    && isFiniteRange(config.renderer.maxPixelRatio, 0, 4)
    && typeof config.renderer.allowHighQuality === "boolean"
    && QUALITY_PROFILES.every((quality) => isQualityCaps(config.renderer.qualityCaps?.[quality]))
    && Boolean(config.telemetry)
    && typeof config.telemetry.galleryPerformanceEnabled === "boolean"
    && isFiniteRange(config.telemetry.galleryPerformanceSampleRate, 0, 1)
    && typeof config.optimizedAssets?.enabled === "boolean"
    && (config.mediaDelivery === undefined || isRuntimeMediaDelivery(config.mediaDelivery))
    && typeof config.progressiveLoading?.enabled === "boolean"
    && typeof config.killSwitches?.disable3DGallery === "boolean"
    && typeof config.killSwitches?.disableGalleryTelemetry === "boolean";
};

let runtimeConfigPromise: Promise<RuntimeConfig> | null = null;

export const getRuntimeConfig = async (): Promise<RuntimeConfig> => {
  if (!runtimeConfigPromise) {
    runtimeConfigPromise = axiosInstance.get("/runtime/config")
      .then((response) => {
        const config = response?.data?.data;
        return isRuntimeConfig(config)
          ? { ...config, mediaDelivery: config.mediaDelivery ?? null, source: "api" as const }
          : DEFAULT_RUNTIME_CONFIG;
      })
      .catch(() => DEFAULT_RUNTIME_CONFIG);
  }

  return runtimeConfigPromise;
};

export const shouldSampleGalleryPerformance = (
  config: RuntimeConfig,
  randomValue = Math.random()
) => config.telemetry.galleryPerformanceEnabled
  && !config.killSwitches.disableGalleryTelemetry
  && randomValue < config.telemetry.galleryPerformanceSampleRate;

export const resetRuntimeConfigCacheForTests = () => {
  runtimeConfigPromise = null;
};
